import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "preact/hooks";

import type {
    MutableRef,
} from "preact/hooks";

import type {
    PayloadFieldConfig,
} from "../jobs/mod.ts";

export type JobStats = {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    cancelled: number;
    total: number;
};

export type JobItem = {
    id: number;
    jobType: string;
    status: string;
    priority: number;
    payload: string;
    result: string | null;
    errorMessage: string | null;
    attempts: number;
    maxAttempts: number;
    lockedBy: string | null;
    lockedAt: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    scheduledFor: string | null;
    progress: number | null;
};

export type JobLogItem = {
    id: number;
    jobId: number;
    level: string;
    message: string;
    data: unknown;
    attempt: number;
    createdAt: string;
};

export type JobTypeInfoItem = {
    jobType: string;
    nextScheduledRun: string | null;
    totalJobs: number;
    failedCount: number;
    lastCompletedAt: string | null;
    label?: string;
    manuallyInvocable?: boolean;
    payloadFields?: PayloadFieldConfig[];
};

export type WorkerItem = {
    threadId: number;
    jobId: number;
    jobType: string;
    startedAt: string;
    running: boolean;
};

export type SessionMinMax = {
    min: number;
    max: number;
};

// columns supported by the workqueue admin endpoint. mirrors VALID_SORT_BYS
// in @mrflark/workqueue's deno-admin-routes.ts (minus the "default" alias).
// passing null leaves the sort param off the request entirely so the server
// falls back to its legacy default ordering.
export type JobSortColumn =
    | "id"
    | "jobType"
    | "status"
    | "createdAt"
    | "scheduledFor"
    | "duration"
    | "priority";

export type JobSortState = {
    column: JobSortColumn;
    direction: "asc" | "desc";
};

type JobStreamEvent = {
    stats: JobStats;
    types: JobTypeInfoItem[];
    workers: WorkerItem[];
    dispatcher: { running: boolean; };
    jobs: JobItem[];
    pagination: { total: number; limit: number; offset: number; };
};

type JobDetailStreamEvent = {
    job: JobItem;
    logs: JobLogItem[];
};

export type WorkqueueOperations = {
    getJobStats: () => Promise<JobStats>;
    getJobTypes: () => Promise<{ types: JobTypeInfoItem[]; }>;
    getWorkers: () => Promise<{ workers: WorkerItem[]; }>;
    getJob: (jobId: number) => Promise<{ job: JobItem; logs: JobLogItem[]; }>;
    retryJob: (jobId: number) => Promise<{ job: JobItem; }>;
    cancelJob: (jobId: number) => Promise<{ job: JobItem; }>;
    runJob: (jobType: string, payload?: unknown) => Promise<unknown>;
    getSettings: () => Promise<{ settings: Record<string, string>; }>;
    updateSetting: (key: string, value: string) => Promise<unknown>;
    getJobsStreamUrl: (params?: Record<string, string>) => string;
    getJobStreamUrl: (jobId: number) => string;
    bulkCancel?: (ids: number[]) => Promise<BulkResult>;
    bulkRetry?: (ids: number[]) => Promise<BulkResult>;
    bulkDelete?: (ids: number[]) => Promise<BulkResult>;
    bulkReschedule?: (
        ids: number[],
        scheduledFor: string,
    ) => Promise<BulkResult>;
};

export type UseJobsStreamOptions = {
    operations?: WorkqueueOperations;
    baseUrl?: string;
    timePeriod?: number;
    streamIntervalMs?: number;
    jobPollIntervalMs?: number;
    enableStream?: boolean;
    pageSize?: number;
    initialSort?: JobSortState | null;
};

export type UseJobsStreamResult = {
    stats: JobStats | null;
    types: JobTypeInfoItem[];
    workers: WorkerItem[];
    dispatcherRunning: boolean;

    jobs: JobItem[];
    totalJobs: number;

    sessionRanges: Record<string, SessionMinMax>;

    page: number;
    pageSize: number;
    typeFilters: string[];
    statusFilters: string[];
    timePeriod: number;
    startTime: string;
    endTime: string;
    streamIntervalMs: number;
    sort: JobSortState | null;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setTypeFilters: (filters: string[]) => void;
    setStatusFilters: (filters: string[]) => void;
    setTimePeriod: (minutes: number) => void;
    setStartTime: (time: string) => void;
    setEndTime: (time: string) => void;
    setStreamIntervalMs: (ms: number) => void;
    setSort: (sort: JobSortState | null) => void;

    selectedJob: JobItem | null;
    selectedJobLogs: JobLogItem[];
    detailLoading: boolean;
    selectJob: (jobId: number | null) => void;

    actionLoading: boolean;
    retryJob: (jobId: number) => Promise<void>;
    cancelJob: (jobId: number) => Promise<void>;
    runJob: (jobType: string, payload?: unknown) => Promise<void>;

    settings: Record<string, string>;
    updateSetting: (key: string, value: string) => Promise<void>;

    refresh: () => void;
    refreshing: boolean;

    bulkCancel: (ids: number[]) => Promise<BulkResult>;
    bulkRetry: (ids: number[]) => Promise<BulkResult>;
    bulkDelete: (ids: number[]) => Promise<BulkResult>;
    bulkReschedule: (
        ids: number[],
        scheduledFor: string,
    ) => Promise<BulkResult>;

    connected: boolean;
    error: string | null;
};

export type BulkResult = {
    succeeded: number;
    skipped: number;
};

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);

// pills look forward 24h to include scheduled/pending jobs
const FUTURE_WINDOW_MS = 24 * 60 * 60 * 1000;

const updateWatermark = (
    ref: MutableRef<Record<string, SessionMinMax>>,
    key: string,
    value: number,
): void => {
    const current = ref.current[key];
    if (!current) {
        ref.current[key] = {
            min: value,
            max: value,
        };
    } else {
        if (value < current.min) current.min = value;
        if (value > current.max) current.max = value;
    }
};

export const useJobsStream = (
    options: UseJobsStreamOptions,
): UseJobsStreamResult => {
    const {
        operations,
        baseUrl,
        timePeriod: initialTimePeriod = 1440,
        streamIntervalMs: initialStreamIntervalMs = 30000,
        jobPollIntervalMs = 5000,
        enableStream = true,
        pageSize: initialPageSize = 25,
        initialSort = null,
    } = options;

    // backward compat: when operations is not provided, construct from baseUrl
    const resolvedOperations = useMemo((): WorkqueueOperations => {
        if (operations) return operations;

        const url = baseUrl!;
        return {
            getJobStats: async () => {
                const response = await fetch(`${url}/stats`);
                return response.json();
            },
            getJobTypes: async () => {
                const response = await fetch(`${url}/types`);
                return response.json();
            },
            getWorkers: async () => {
                const response = await fetch(`${url}/workers`);
                return response.json();
            },
            getJob: async (jobId: number) => {
                const response = await fetch(`${url}/${jobId}`);
                return response.json();
            },
            retryJob: async (jobId: number) => {
                const response = await fetch(`${url}/${jobId}/retry`, {
                    method: "POST",
                });
                if (!response.ok) throw new Error("Retry failed");
                return response.json();
            },
            cancelJob: async (jobId: number) => {
                const response = await fetch(`${url}/${jobId}/cancel`, {
                    method: "POST",
                });
                if (!response.ok) throw new Error("Cancel failed");
                return response.json();
            },
            runJob: async (jobType: string, payload?: unknown) => {
                const response = await fetch(`${url}/run`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        jobType,
                        payload: payload ?? {},
                    }),
                });
                if (!response.ok) throw new Error("Run failed");
                return response.json();
            },
            getSettings: async () => {
                const response = await fetch(`${url}/../settings`);
                return response.json();
            },
            updateSetting: async (key: string, value: string) => {
                const response = await fetch(`${url}/../settings/${key}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        value,
                    }),
                });
                if (!response.ok) throw new Error("Setting update failed");
                return response.json();
            },
            getJobsStreamUrl: (params?: Record<string, string>) => {
                const query = params
                    ? `?${new URLSearchParams(params).toString()}`
                    : "";
                return `${url}/stream${query}`;
            },
            getJobStreamUrl: (jobId: number) => {
                return `${url}/${jobId}/stream`;
            },
            bulkCancel: async (ids: number[]) => {
                const response = await fetch(`${url}/bulk/cancel`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        ids,
                    }),
                });
                if (!response.ok) throw new Error("Bulk cancel failed");
                return response.json();
            },
            bulkRetry: async (ids: number[]) => {
                const response = await fetch(`${url}/bulk/retry`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        ids,
                    }),
                });
                if (!response.ok) throw new Error("Bulk retry failed");
                return response.json();
            },
            bulkDelete: async (ids: number[]) => {
                const response = await fetch(`${url}/bulk/delete`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        ids,
                    }),
                });
                if (!response.ok) throw new Error("Bulk delete failed");
                return response.json();
            },
            bulkReschedule: async (ids: number[], scheduledFor: string) => {
                const response = await fetch(`${url}/bulk/reschedule`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        ids,
                        scheduledFor,
                    }),
                });
                if (!response.ok) throw new Error("Bulk reschedule failed");
                return response.json();
            },
        };
    }, [operations, baseUrl]);

    const [stats, setStats] = useState<JobStats | null>(null);
    const [types, setTypes] = useState<JobTypeInfoItem[]>([]);
    const [workers, setWorkers] = useState<WorkerItem[]>([]);
    const [dispatcherRunning, setDispatcherRunning] = useState(false);

    const [jobs, setJobs] = useState<JobItem[]>([]);
    const [totalJobs, setTotalJobs] = useState(0);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [typeFilters, setTypeFiltersRaw] = useState<string[]>([]);
    const [statusFilters, setStatusFiltersRaw] = useState<string[]>([]);
    const [timePeriod, setTimePeriodRaw] = useState(initialTimePeriod);
    const [startTime, setStartTimeRaw] = useState("");
    const [endTime, setEndTimeRaw] = useState("");
    const [streamIntervalMs, setStreamIntervalMs] = useState(
        initialStreamIntervalMs,
    );
    const [sort, setSortRaw] = useState<JobSortState | null>(initialSort);
    const [reconnectTick, setReconnectTick] = useState(0);

    const [selectedJob, setSelectedJob] = useState<JobItem | null>(null);
    const [selectedJobLogs, setSelectedJobLogs] = useState<JobLogItem[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const selectedJobIdRef = useRef<number | null>(null);

    const [actionLoading, setActionLoading] = useState(false);
    const [settings, setSettings] = useState<Record<string, string>>({});
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    const watermarkRef = useRef<Record<string, SessionMinMax>>({});
    const [sessionRanges, setSessionRanges] = useState<
        Record<string, SessionMinMax>
    >({});

    // wrapped setters enforce mutual exclusivity between pills and custom dates
    const setTimePeriod = useCallback((minutes: number): void => {
        setTimePeriodRaw(minutes);
        setStartTimeRaw("");
        setEndTimeRaw("");
        setPage(1);
    }, []);

    const setStartTime = useCallback((time: string): void => {
        setStartTimeRaw(time);
        setTimePeriodRaw(0);
        setPage(1);
    }, []);

    const setEndTime = useCallback((time: string): void => {
        setEndTimeRaw(time);
        setTimePeriodRaw(0);
        setPage(1);
    }, []);

    const refresh = useCallback((): void => {
        setRefreshing(true);
        setReconnectTick((prev) => prev + 1);
    }, []);

    const setTypeFilters = useCallback((filters: string[]): void => {
        setTypeFiltersRaw(filters);
        setPage(1);
    }, []);

    const setStatusFilters = useCallback((filters: string[]): void => {
        setStatusFiltersRaw(filters);
        setPage(1);
    }, []);

    // changing sort always returns to the first page so the user sees the
    // top of the new ordering immediately
    const setSort = useCallback((next: JobSortState | null): void => {
        setSortRaw(next);
        setPage(1);
    }, []);

    // refresh relative time windows every 60s so the window doesn't drift
    useEffect(() => {
        if (timePeriod <= 0) return;
        const timer = setInterval(
            () => setReconnectTick((prev) => prev + 1),
            60000,
        );
        return (): void => clearInterval(timer);
    }, [timePeriod]);

    const loadSettings = useCallback(async (): Promise<void> => {
        try {
            const data = await resolvedOperations.getSettings();
            setSettings(data.settings ?? data);
        } catch { /* ignore */ }
    }, [resolvedOperations]);

    useEffect(() => {
        if (!enableStream) return;

        const params: Record<string, string> = {};

        // compute absolute dates from pill or custom range
        if (timePeriod > 0) {
            const now = new Date();
            const start = new Date(now.getTime() - timePeriod * 60 * 1000);
            const end = new Date(now.getTime() + FUTURE_WINDOW_MS);
            params.startTime = start.toISOString();
            params.endTime = end.toISOString();
        } else {
            if (startTime) params.startTime = startTime;
            if (endTime) params.endTime = endTime;
        }

        if (typeFilters.length > 0) params.type = typeFilters.join(",");
        if (statusFilters.length > 0) params.status = statusFilters.join(",");
        params.limit = String(pageSize);
        params.offset = String((page - 1) * pageSize);
        if (streamIntervalMs) params.interval = String(streamIntervalMs);
        if (sort) {
            params.sortBy = sort.column;
            params.sortOrder = sort.direction;
        }

        const streamUrl = resolvedOperations.getJobsStreamUrl(params);
        const eventSource = new EventSource(streamUrl);

        eventSource.onopen = (): void => {
            setConnected(true);
        };

        eventSource.onmessage = (event): void => {
            try {
                const data = JSON.parse(event.data) as JobStreamEvent;
                setStats(data.stats);
                setTypes(data.types);
                setWorkers(data.workers);
                setDispatcherRunning(data.dispatcher.running);
                setJobs(data.jobs);
                setTotalJobs(data.pagination.total);

                updateWatermark(watermarkRef, "pending", data.stats.pending);
                updateWatermark(
                    watermarkRef,
                    "processing",
                    data.stats.processing,
                );
                updateWatermark(watermarkRef, "failed", data.stats.failed);
                updateWatermark(
                    watermarkRef,
                    "activeWorkers",
                    data.workers.filter((worker) => worker.running).length,
                );
                setSessionRanges({
                    ...watermarkRef.current,
                });
                setRefreshing(false);
            } catch { /* ignore */ }
        };

        eventSource.onerror = (): void => {
            setConnected(false);
        };

        return (): void => {
            eventSource.close();
            setConnected(false);
        };
    }, [
        resolvedOperations,
        timePeriod,
        typeFilters,
        statusFilters,
        startTime,
        endTime,
        page,
        pageSize,
        streamIntervalMs,
        enableStream,
        reconnectTick,
        sort,
    ]);

    useEffect(() => {
        if (enableStream) return;

        const pollStats = async (): Promise<void> => {
            try {
                const [statsData, typesData, workersData] = await Promise.all([
                    resolvedOperations.getJobStats(),
                    resolvedOperations.getJobTypes(),
                    resolvedOperations.getWorkers(),
                ]);
                setStats(statsData);
                setTypes(typesData.types);
                setWorkers(workersData.workers);
            } catch { /* ignore */ }
        };

        pollStats();
        const interval = setInterval(pollStats, jobPollIntervalMs);
        return (): void => clearInterval(interval);
    }, [resolvedOperations, enableStream, jobPollIntervalMs]);

    // load settings on mount
    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        const jobId = selectedJobIdRef.current;
        if (jobId === null) return;
        if (selectedJob && TERMINAL_STATUSES.has(selectedJob.status)) return;

        setDetailLoading(true);

        const eventSource = new EventSource(
            resolvedOperations.getJobStreamUrl(jobId),
        );

        eventSource.onmessage = (event): void => {
            try {
                const data = JSON.parse(event.data) as JobDetailStreamEvent;
                setSelectedJob(data.job);
                setSelectedJobLogs(data.logs);
                setDetailLoading(false);

                if (TERMINAL_STATUSES.has(data.job.status)) {
                    eventSource.close();
                }
            } catch { /* ignore */ }
        };

        eventSource.onerror = (): void => {
            setDetailLoading(false);
            eventSource.close();

            resolvedOperations.getJob(jobId)
                .then((data) => {
                    setSelectedJob(data.job);
                    setSelectedJobLogs(data.logs ?? []);
                })
                .catch(() => {/* ignore */});
        };

        return (): void => {
            eventSource.close();
        };
    }, [resolvedOperations, selectedJobIdRef.current, selectedJob?.status]);

    const selectJob = useCallback((jobId: number | null): void => {
        selectedJobIdRef.current = jobId;
        if (jobId === null) {
            setSelectedJob(null);
            setSelectedJobLogs([]);
            return;
        }
        setDetailLoading(true);
    }, []);

    const retryJob = useCallback(async (jobId: number): Promise<void> => {
        setActionLoading(true);
        try {
            const data = await resolvedOperations.retryJob(jobId);
            setSelectedJob(data.job);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Retry failed");
        } finally {
            setActionLoading(false);
        }
    }, [resolvedOperations]);

    const cancelJob = useCallback(async (jobId: number): Promise<void> => {
        setActionLoading(true);
        try {
            const data = await resolvedOperations.cancelJob(jobId);
            setSelectedJob(data.job);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Cancel failed");
        } finally {
            setActionLoading(false);
        }
    }, [resolvedOperations]);

    const runJob = useCallback(
        async (jobType: string, payload?: unknown): Promise<void> => {
            setActionLoading(true);
            try {
                await resolvedOperations.runJob(jobType, payload);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Run failed");
            } finally {
                setActionLoading(false);
            }
        },
        [resolvedOperations],
    );

    const updateSetting = useCallback(
        async (key: string, value: string): Promise<void> => {
            try {
                await resolvedOperations.updateSetting(key, value);
                setSettings((prev) => ({
                    ...prev,
                    [key]: value,
                }));
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Setting update failed",
                );
            }
        },
        [resolvedOperations],
    );

    const bulkCancel = useCallback(
        async (ids: number[]): Promise<BulkResult> => {
            setActionLoading(true);
            try {
                const result = await resolvedOperations.bulkCancel!(ids);
                setReconnectTick((prev) => prev + 1);
                return result;
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "Bulk cancel failed",
                );
                return {
                    succeeded: 0,
                    skipped: ids.length,
                };
            } finally {
                setActionLoading(false);
            }
        },
        [resolvedOperations],
    );

    const bulkRetry = useCallback(
        async (ids: number[]): Promise<BulkResult> => {
            setActionLoading(true);
            try {
                const result = await resolvedOperations.bulkRetry!(ids);
                setReconnectTick((prev) => prev + 1);
                return result;
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "Bulk retry failed",
                );
                return {
                    succeeded: 0,
                    skipped: ids.length,
                };
            } finally {
                setActionLoading(false);
            }
        },
        [resolvedOperations],
    );

    const bulkDelete = useCallback(
        async (ids: number[]): Promise<BulkResult> => {
            setActionLoading(true);
            try {
                const result = await resolvedOperations.bulkDelete!(ids);
                setReconnectTick((prev) => prev + 1);
                return result;
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "Bulk delete failed",
                );
                return {
                    succeeded: 0,
                    skipped: ids.length,
                };
            } finally {
                setActionLoading(false);
            }
        },
        [resolvedOperations],
    );

    const bulkReschedule = useCallback(
        async (ids: number[], scheduledFor: string): Promise<BulkResult> => {
            setActionLoading(true);
            try {
                const result = await resolvedOperations.bulkReschedule!(
                    ids,
                    scheduledFor,
                );
                setReconnectTick((prev) => prev + 1);
                return result;
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Bulk reschedule failed",
                );
                return {
                    succeeded: 0,
                    skipped: ids.length,
                };
            } finally {
                setActionLoading(false);
            }
        },
        [resolvedOperations],
    );

    return {
        stats,
        types,
        workers,
        dispatcherRunning,
        jobs,
        totalJobs,
        sessionRanges,
        page,
        pageSize,
        typeFilters,
        statusFilters,
        timePeriod,
        startTime,
        endTime,
        streamIntervalMs,
        sort,
        setPage,
        setPageSize,
        setTypeFilters,
        setStatusFilters,
        setTimePeriod,
        setStartTime,
        setEndTime,
        setStreamIntervalMs,
        setSort,
        selectedJob,
        selectedJobLogs,
        detailLoading,
        selectJob,
        actionLoading,
        retryJob,
        cancelJob,
        runJob,
        settings,
        updateSetting,
        refresh,
        refreshing,
        bulkCancel,
        bulkRetry,
        bulkDelete,
        bulkReschedule,
        connected,
        error,
    };
};
